<template>
  <div
    class="ask-question flex flex-wrap items-center gap-x-3 gap-y-1.5"
    :class="[variant === 'compact' ? 'text-xs' : 'text-sm', tone === 'on-dark' && '[text-shadow:0_1px_3px_rgb(0_0_0_/_0.55)]']"
  >
    <span
      class="font-medium"
      :class="tone === 'on-dark' ? 'text-white/85' : 'text-slate-500 dark:text-slate-400'"
    >{{ t('askQuestion.label') }}</span>

    <span class="inline-flex items-center gap-2">
      <a
        :href="waUrl"
        target="_blank"
        rel="noopener"
        class="group inline-flex items-center gap-1.5 font-medium transition-colors"
        :class="tone === 'on-dark'
          ? 'text-white hover:text-green-300'
          : 'text-slate-700 dark:text-slate-200 hover:text-green-600 dark:hover:text-green-400'"
        :aria-label="title
          ? t('askQuestion.whatsappAria', { title })
          : t('askQuestion.whatsappAriaGeneric')"
        @click="onClick('whatsapp')"
      >
        <Icon
          name="mdi:whatsapp"
          class="size-4 transition-transform group-hover:scale-110"
          :class="tone === 'on-dark' ? 'text-green-400' : 'text-green-600'"
        />
        {{ t('askQuestion.whatsapp') }}
      </a>

      <template v-if="tgUrl">
        <span :class="tone === 'on-dark' ? 'text-white/40' : 'text-slate-300 dark:text-slate-600'" aria-hidden="true">·</span>
        <a
          :href="tgUrl"
          target="_blank"
          rel="noopener"
          class="group inline-flex items-center gap-1.5 font-medium transition-colors"
          :class="tone === 'on-dark'
            ? 'text-white hover:text-sky-300'
            : 'text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400'"
          :aria-label="title
            ? t('askQuestion.telegramAria', { title })
            : t('askQuestion.telegramAriaGeneric')"
          @click="onClick('telegram')"
        >
          <Icon
            name="ic:baseline-telegram"
            class="size-4 transition-transform group-hover:scale-110"
            :class="tone === 'on-dark' ? 'text-sky-300' : 'text-sky-500'"
          />
          {{ t('askQuestion.telegram') }}
        </a>
      </template>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  buildQuestionWhatsappUrl,
  buildQuestionTelegramUrl,
  type QuestionInput,
} from '~/utils/booking-message';

// Low-commitment "Ask a question" entry point. Renders a WhatsApp (+ Telegram,
// when configured) deep-link pre-filled with a soft, open-ended question via
// utils/booking-message. The apartment context is optional — omit listingId/
// title on generic surfaces like the home hero.
type Props = {
  placement: string;
  listingId?: number | string;
  title?: string;
  checkin?: string;
  checkout?: string;
  guests?: number;
  variant?: 'inline' | 'compact';
  tone?: 'default' | 'on-dark';
};
const props = withDefaults(defineProps<Props>(), {
  variant: 'inline',
  tone: 'default',
  listingId: undefined,
  title: undefined,
  checkin: undefined,
  checkout: undefined,
  guests: undefined,
});

const { locale, t } = useI18n();
const config = useRuntimeConfig();
const { trackEvent } = useAnalytics();

const input = computed<QuestionInput>(() => ({
  apartmentTitle: props.title,
  listingId: props.listingId,
  locale: locale.value,
  ...(props.checkin && props.checkout
    ? { checkin: props.checkin, checkout: props.checkout, guests: props.guests }
    : {}),
}));

const waUrl = computed(() =>
  buildQuestionWhatsappUrl((config.public.whatsappNumber as string) || '', input.value),
);

const tgUrl = computed(() =>
  buildQuestionTelegramUrl((config.public.telegramLink as string) || '', input.value),
);

function onClick(channel: 'whatsapp' | 'telegram') {
  trackEvent('ask_question_click', {
    placement: props.placement,
    channel,
    listing_id: props.listingId ?? undefined,
  });
}
</script>
